import { useState, useEffect } from 'react';
import Modal from '@/components/base/Modal';
import { teacherRoles, classSections, type TeacherRecord } from '@/mocks/teachers';
import { students } from '@/mocks/students';

interface TeacherDetailModalProps {
  open: boolean;
  teacher: TeacherRecord | null;
  onClose: () => void;
  onSave: (updated: TeacherRecord) => void;
  onViewStudents?: (teacherName: string) => void;
}

const statusOptions: { value: TeacherRecord['status']; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'on-leave', label: 'On Leave' },
  { value: 'inactive', label: 'Inactive' },
];

export default function TeacherDetailModal({
  open,
  teacher,
  onClose,
  onSave,
  onViewStudents,
}: TeacherDetailModalProps) {
  const [formData, setFormData] = useState<Partial<TeacherRecord>>({});

  useEffect(() => {
    if (teacher) {
      setFormData({ ...teacher });
    }
  }, [teacher, open]);

  const studentCount = teacher
    ? students.filter((s) => s.classTeacher === teacher.name).length
    : 0;

  const updateField = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    if (!teacher || !formData.name || !formData.role || !formData.className) return;
    const newStatus = (formData.status as TeacherRecord['status']) || teacher.status;
    const wasInactive = teacher.status === 'inactive';
    const isNowInactive = newStatus === 'inactive';
    const updated: TeacherRecord = {
      ...teacher,
      name: formData.name,
      dob: formData.dob || teacher.dob,
      role: formData.role,
      className: formData.className,
      dateOfJoining: formData.dateOfJoining || teacher.dateOfJoining,
      contactNumber: formData.contactNumber || teacher.contactNumber,
      email: formData.email || teacher.email,
      address: formData.address || teacher.address,
      district: formData.district || teacher.district,
      town: formData.town || teacher.town,
      status: newStatus,
      deactivatedAt: isNowInactive
        ? (teacher.deactivatedAt || new Date().toISOString().split('T')[0])
        : (wasInactive && !isNowInactive ? undefined : teacher.deactivatedAt),
    };
    onSave(updated);
    onClose();
  };

  if (!teacher) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit Teacher"
      subtitle={teacher.teacherId}
      size="xl"
      footer={
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-label font-medium ${
              (formData.status || teacher.status) === 'active'
                ? 'bg-semantic-success-bg text-semantic-success'
                : (formData.status || teacher.status) === 'on-leave'
                  ? 'bg-semantic-warning-bg text-semantic-warning'
                  : 'bg-semantic-error-bg text-semantic-error'
            }`}>
              <span className={`w-1.5 h-1.5 rounded-full ${
                (formData.status || teacher.status) === 'active'
                  ? 'bg-semantic-success'
                  : (formData.status || teacher.status) === 'on-leave'
                    ? 'bg-semantic-warning'
                    : 'bg-semantic-error'
              }`} />
              {statusOptions.find(s => s.value === (formData.status || teacher.status))?.label}
            </span>
          </div>
          <div className="flex items-center gap-3">
            {(formData.status || teacher.status) !== 'inactive' && (
              <button
                type="button"
                onClick={() => {
                  const updated: TeacherRecord = {
                    ...teacher,
                    name: formData.name || teacher.name,
                    dob: formData.dob || teacher.dob,
                    role: formData.role || teacher.role,
                    className: formData.className || teacher.className,
                    dateOfJoining: formData.dateOfJoining || teacher.dateOfJoining,
                    contactNumber: formData.contactNumber || teacher.contactNumber,
                    email: formData.email || teacher.email,
                    address: formData.address || teacher.address,
                    district: formData.district || teacher.district,
                    town: formData.town || teacher.town,
                    status: 'inactive',
                    deactivatedAt: teacher.deactivatedAt || new Date().toISOString().split('T')[0],
                  };
                  onSave(updated);
                  onClose();
                }}
                className="inline-flex items-center gap-2 h-10 px-4 bg-semantic-error-bg text-semantic-error text-sm font-label font-medium rounded-lg hover:bg-semantic-error/20 transition-colors cursor-pointer whitespace-nowrap border border-semantic-error/30"
              >
                <i className="ri-user-unfollow-line" />
                Deactivate
              </button>
            )}
            <button
              onClick={onClose}
              className="inline-flex items-center gap-2 h-10 px-4 bg-background-100 text-foreground-600 text-sm font-label font-medium rounded-lg hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="inline-flex items-center gap-2 h-10 px-5 bg-primary-500 text-background-50 text-sm font-label font-medium rounded-lg hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-save-line" />
              Save Changes
            </button>
          </div>
        </div>
      }
    >
      <div className="space-y-6">
        {/* ── Profile Section ─────────────────────────────── */}
        <div>
          <h3 className="text-sm font-label font-semibold text-foreground-700 uppercase tracking-wide mb-4 flex items-center gap-2">
            <i className="ri-user-line text-foreground-500" />
            Profile Information
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground-700 font-label">Full Name</label>
              <input
                type="text"
                value={formData.name || ''}
                onChange={(e) => updateField('name', e.target.value)}
                className="xl-input"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground-700 font-label">Date of Birth</label>
              <input
                type="date"
                value={formData.dob || ''}
                onChange={(e) => updateField('dob', e.target.value)}
                className="xl-input"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground-700 font-label">Role</label>
              <select
                value={formData.role || ''}
                onChange={(e) => updateField('role', e.target.value)}
                className="xl-input cursor-pointer"
              >
                {teacherRoles.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground-700 font-label">Status</label>
              <select
                value={formData.status || teacher.status}
                onChange={(e) => updateField('status', e.target.value)}
                className="xl-input cursor-pointer"
              >
                {statusOptions.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground-700 font-label">Contact Number</label>
              <input
                type="text"
                value={formData.contactNumber || ''}
                onChange={(e) => updateField('contactNumber', e.target.value)}
                className="xl-input"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground-700 font-label">Email Address</label>
              <input
                type="email"
                value={formData.email || ''}
                onChange={(e) => updateField('email', e.target.value)}
                className="xl-input"
              />
            </div>
            <div className="flex flex-col gap-1.5 sm:col-span-2">
              <label className="text-sm font-medium text-foreground-700 font-label">Address</label>
              <input
                type="text"
                value={formData.address || ''}
                onChange={(e) => updateField('address', e.target.value)}
                className="xl-input"
              />
            </div>
          </div>
        </div>

        {/* ── Assignment Section ───────────────────────────── */}
        <div className="border-t border-background-200 pt-6">
          <h3 className="text-sm font-label font-semibold text-foreground-700 uppercase tracking-wide mb-4 flex items-center gap-2">
            <i className="ri-briefcase-line text-foreground-500" />
            Assignment Details
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground-700 font-label">Class / Section</label>
              <select
                value={formData.className || ''}
                onChange={(e) => updateField('className', e.target.value)}
                className="xl-input cursor-pointer"
              >
                {classSections.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground-700 font-label">Date of Joining</label>
              <input
                type="date"
                value={formData.dateOfJoining || ''}
                onChange={(e) => updateField('dateOfJoining', e.target.value)}
                className="xl-input"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground-700 font-label">District</label>
              <input
                type="text"
                value={formData.district || ''}
                onChange={(e) => updateField('district', e.target.value)}
                className="xl-input"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground-700 font-label">Town / City</label>
              <input
                type="text"
                value={formData.town || ''}
                onChange={(e) => updateField('town', e.target.value)}
                className="xl-input"
              />
            </div>
          </div>
        </div>

        {/* ── Student Count ────────────────────────────────── */}
        <div className="border-t border-background-200 pt-6 mt-6">
          <div className="flex items-center justify-between p-4 bg-background-100 rounded-xl">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100">
                <i className="ri-group-line text-lg text-primary-600" />
              </div>
              <div>
                <p className="text-sm font-label font-semibold text-foreground-800">
                  {studentCount} student{studentCount !== 1 ? 's' : ''} assigned
                </p>
                <p className="text-xs text-foreground-500 font-label">
                  This teacher has {studentCount} student{studentCount !== 1 ? 's' : ''} in {teacher.className}
                </p>
              </div>
            </div>
            {studentCount > 0 && onViewStudents && (
              <button
                type="button"
                onClick={() => onViewStudents(teacher.name)}
                className="inline-flex items-center gap-2 h-9 px-4 bg-primary-500 text-background-50 text-xs font-label font-medium rounded-lg hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-external-link-line text-sm" />
                View Students
              </button>
            )}
            {studentCount === 0 && (
              <span className="text-xs font-label text-foreground-400">No students enrolled yet</span>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}